import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Sparkles, Martini, BookOpen, Bell, ArrowUpRight, Instagram } from "lucide-react";
import { ASSETS } from "../lib/content";

const Row = ({ icon: Icon, label, sub, onClick, to, delay, testId }) => {
  const inner = (
    <>
      <span className="w-10 h-10 rounded-full bg-void border border-ember/30 flex items-center justify-center text-ember shrink-0 group-hover:bg-ember group-hover:text-void transition-colors">
        <Icon size={17} />
      </span>
      <span className="flex-1 text-left">
        <span className="block font-display font-extrabold uppercase tracking-tight text-cream text-base leading-tight group-hover:text-ember transition-colors">{label}</span>
        <span className="block font-mono text-[10px] text-cream/45 mt-0.5">{sub}</span>
      </span>
      <ArrowUpRight size={16} className="text-cream/30 group-hover:text-ember transition-colors shrink-0" />
    </>
  );
  const cls = "group w-full flex items-center gap-4 bg-ash border border-white/10 rounded-2xl px-5 py-4 hover:border-ember/60 transition-colors";
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay, ease: [0.16, 1, 0.3, 1] }}>
      {to ? (
        <Link to={to} state={to === "/" ? { scrollTo: "#shop" } : undefined} data-testid={testId} className={cls}>{inner}</Link>
      ) : (
        <button onClick={onClick} data-testid={testId} className={cls}>{inner}</button>
      )}
    </motion.div>
  );
};

export default function LinksPage({ onJoin }) {
  const navigate = useNavigate();

  return (
    <main data-testid="links-page" className="min-h-screen flex flex-col items-center px-5 pt-16 pb-14">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="flex flex-col items-center text-center">
        <img src={ASSETS.mascot} alt="Gremlin Coffee" className="w-24 h-24 rounded-full object-cover ring-2 ring-ember" />
        <h1 className="font-display font-black uppercase tracking-tight text-cream text-3xl mt-5">
          Gremlin<span className="text-ember"> Coffee</span>
        </h1>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-cream/65 mt-2">
          Air roasted · Nashville · Ships North America
        </p>
        <p className="font-mono text-xs text-ember mt-3">Smooth coffee. Gremlin energy.</p>
      </motion.div>

      <div className="w-full max-w-md flex flex-col gap-3 mt-10">
        <Row icon={ShoppingBag} label="Shop the Beans" sub="3 air-roasted flavors · launching soon · join the club for first dibs" to="/" delay={0.05} testId="links-shop" />
        <Row icon={Sparkles} label="Take the Flavor Quiz" sub="Which gremlin are you? 5 questions, no wrong answers" to="/quiz" delay={0.12} testId="links-quiz" />
        <Row icon={Martini} label="Espresso Martini Recipes" sub="Cocktails & brews built around the beans" to="/recipes" delay={0.19} testId="links-recipes" />
        <Row icon={BookOpen} label="The Journal" sub="Roasting science, Nashville mischief & brand notes" to="/journal" delay={0.26} testId="links-journal" />
        <Row icon={Bell} label="Join the Coffee Club" sub="First dibs on drops, merch & subscriber pricing" onClick={onJoin} delay={0.33} testId="links-club" />
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex items-center gap-5 mt-10">
        <a href="https://instagram.com/gremlincoffeeco" target="_blank" rel="noreferrer" data-testid="links-instagram" aria-label="Instagram" className="text-cream/65 hover:text-ember transition-colors"><Instagram size={20} /></a>
      </motion.div>

      <button
        onClick={() => navigate("/")}
        data-testid="links-full-site"
        className="mt-8 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/55 hover:text-ember transition-colors"
      >
        gremlincoffee.com → full site
      </button>
    </main>
  );
}
